<script lang="ts">
export default { inheritAttrs: false };
</script>

<script lang="ts" setup>
const emit = defineEmits<{
  (event: 'mouseenter'): void;
  (event: 'mouseleave'): void;
  (event: 'update:state', value: boolean): void;
  (event: 'open'): void;
  (event: 'close'): void;
  (event: 'hardclose'): void;
}>();

const props = withDefaults(
  defineProps<{
    state?: boolean | undefined;
    disabled?: boolean;
    id?: string;
    teleportTarget?: string;
    showTrigger?: boolean;
    showFooter?: boolean;
  }>(),
  {
    state: undefined,
    disabled: false,
    id: () => useRandomString(),
    teleportTarget: 'body',
    showTrigger: true,
    showFooter: true,
  },
);

// TODO: Add (for mobile): Button to resize Modal

const id = computed(() => `TwModal_${props.id}`);
const scopeId = computed(() => getCurrentInstance()?.vnode.scopeId || '');

const internalState = ref(typeof props.state !== 'undefined' ? props.state : false);
const state = computed({
  get: () => (typeof props.state !== 'undefined' ? props.state : internalState.value),
  set: (v: boolean) => {
    if (v !== state.value) {
      emit('update:state', (internalState.value = v));
      internalState.value ? emit('open') : emit('close');
    }
  },
});
const toggle = async () => (state.value ? close() : open());
const open = async () => {
  const previousState = state.value;
  state.value = true;

  if (!previousState) {
    await nextTick(() => {
      const el = document.querySelector(`#${id.value}`) as HTMLElement | null;
      if (el) el.focus();
    });
  }
};
const close = async () => {
  const previousState = state.value;
  state.value = false;

  if (previousState && document.activeElement) {
    const el = document.querySelector(triggerElementSelector.value) as HTMLElement | null;
    if (el && document.activeElement.closest(`#${id.value}`)) el.focus();
  }
};

const triggerAttrs = computed(() => ({ disabled: props.disabled, class: 'tw-modal--trigger', 'aria-controls': id.value, 'aria-expanded': state.value }));
const triggerElementSelector = computed(() => `.tw-modal--trigger[aria-controls='${id.value}']`);

const { class: hoverClass, events: hoverEvents } = useHover(emit, props);

function hardclose() {
  state.value = false;
}

defineExpose({ state, toggle, open, close, hardclose });
</script>

<template>
  <slot :attrs="triggerAttrs" :close="close" :open="open" :state="state" :toggle="toggle" name="trigger" />

  <Teleport :to="teleportTarget">
    <Transition>
      <div v-if="state" :id="`${id}_Backdrop`" class="tw-modal--backdrop" v-bind="{ [scopeId]: true }" @click="close()" @keydown.enter="close()">
        <div
          :id="id"
          :aria-disabled="props.disabled"
          :aria-hidden="!state"
          :class="['tw-modal', props.disabled && 'is--disabled', hoverClass]"
          aria-modal="true"
          role="dialog"
          tabindex="-1"
          v-bind="{ [scopeId]: true, ...$attrs }"
          v-on="hoverEvents"
          @click.stop
          @keydown.esc.stop="close"
          @keydown.enter.stop
        >
          <header class="tw-modal--header">
            <slot :attrs="{ class: 'tw-modal--actions' }" :close="close" :open="open" :state="state" :toggle="toggle" name="header-prefix" />
            <div v-if="$slots['header-action-left'] && $slots['header-action-left']()?.length">
              <slot name="header-action-left"></slot>
            </div>
            <strong class="tw-modal--title">
              <slot :close="close" :open="open" :state="state" :toggle="toggle" name="title" />
            </strong>
            <div class="tw-modal--actions">
              <tw-ui-button :aria-label="$t('common.close')" :title="$t('common.close')" class="action--close tw-box-variant--circle" @click="toggle()">
                <Icon name="bi:x" />
              </tw-ui-button>
            </div>
          </header>

          <slot name="content" :close="close" :open="open" :state="state" :toggle="toggle">
            <section class="tw-modal--content">
              <slot :close="close" :open="open" :state="state" :toggle="toggle" />
            </section>
          </slot>

          <slot name="footer-wrapper">
            <footer v-if="showFooter" class="tw-modal--footer">
              <slot :hardclose="hardclose" :close="close" :open="open" :state="state" :toggle="toggle" name="footer" />
            </footer>
          </slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/tw-variables.scss';
@import '~/assets/styles/tw-mixins.scss';
@import '~/assets/styles/tw-form.scss';

$tw-component--gap: 0.25em;
$lightGray: #dcdcdcff;

.icon--x {
  height: 1em;
}

.tw-modal--backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 0;
  display: flex;
  z-index: 10000;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.tw-modal {
  bottom: 0;
  width: 100vw;
  display: flex;
  cursor: default;
  max-height: 75vh;
  overflow-y: auto;
  position: absolute;
  padding-bottom: 2rem;
  background-color: #fff;
  flex-flow: column nowrap;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 0.35rem;
  border-top-right-radius: 0.35rem;

  @include tw-is-desktop {
    width: unset;
    bottom: unset;
    max-width: 75vh;
    padding-bottom: 0rem;
    border-bottom-left-radius: 0.35rem;
    border-bottom-right-radius: 0.35rem;
  }
}

.tw-modal--header,
.tw-modal--content,
.tw-modal--footer {
  padding: calc(#{$tw-component--gap} * 3);
}

.tw-modal--header,
.tw-modal--footer,
.tw-modal--actions {
  display: flex;
  align-items: center;
  flex-flow: row nowrap;
  justify-content: center;
  gap: calc(#{$tw-component--gap} * 2);
}

.tw-modal--header {
  flex: 0;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid $lightGray;
  align-items: center;
}

.tw-modal--footer {
  flex: 0;
  align-items: flex-end;
  justify-content: space-between;
  border-top: 1px solid $lightGray;

  &:not(:has(*)) {
    display: none;
  }
}

.tw-modal--content {
  flex: 1;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tw-modal--actions {
  align-self: flex-start;

  &:first-child {
    justify-content: flex-start;
  }

  &:last-child {
    justify-content: flex-end;
  }
}

.tw-modal--title {
  flex: 1;
  font-size: 1.3em;
  align-self: center;
  text-overflow: ellipsis;

  :not(:first-child) {
    text-align: center;
  }
}

.tw-button.action--close {
  flex: 0;
  font-size: 1.5em;
  border-color: transparent;
  background-color: transparent;
  padding: $tw-component--gap;
}
</style>
